import { useEffect, useState, useMemo, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { FaCalendar, FaSearch, FaSort, FaUsers, FaUserPlus, FaTimes } from "react-icons/fa";

const BASE_URL = "https://us-central1-workexpress-1ffff.cloudfunctions.net/app";
const STORAGE_KEY = 'bookingsListState';
const BOOKINGS_CACHE_KEY = 'bookingsDataCache';
const CACHE_DURATION = 30000; // 30 seconds cache duration
const DEFAULT_IMAGE = "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";

const Bookings = () => {
    const [bookings, setBookings] = useState([]);
    const [users, setUsers] = useState([]);
    const [servicemen, setServicemen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const scrollContainerRef = useRef(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedServiceman, setSelectedServiceman] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const isFetchingRef = useRef(false);
    const intervalRef = useRef(null);
    
    // Restore state from sessionStorage on mount
    const getStoredState = () => {
        try {
            const stored = sessionStorage.getItem(STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.error('Error restoring state:', e);
        }
        return null;
    };

    // Get cached bookings data
    const getCachedBookings = () => {
        try {
            const cached = localStorage.getItem(BOOKINGS_CACHE_KEY);
            if (cached) {
                const { data, timestamp } = JSON.parse(cached);
                const now = Date.now();
                // Return cached data if it's less than CACHE_DURATION old
                if (now - timestamp < CACHE_DURATION) {
                    return data;
                }
            }
        } catch (e) {
            console.error('Error reading cache:', e);
        }
        return null;
    };

    // Clear old cache entries to free up space
    const clearOldCache = () => {
        try {
            const cacheKeys = ['customersDataCache', 'servicemenDataCache', 'dashboardDataCache', 'dashboardStatsCache', 'bookingsDataCache'];
            cacheKeys.forEach(key => {
                try {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        const { timestamp } = JSON.parse(cached);
                        const now = Date.now();
                        // Clear cache older than 1 minute
                        if (now - timestamp > 60000) {
                            localStorage.removeItem(key);
                        }
                    }
                } catch (e) {
                    // Ignore errors for individual cache entries
                }
            });
        } catch (e) {
            // Ignore errors
        }
    };

    // Save bookings to cache with quota handling
    const saveToCache = (bookingsData, usersData, servicemenData) => {
        try {
            clearOldCache();
            
            // Reduce data size by only storing essential fields
            const reducedBookings = bookingsData.map(item => ({
                id: item.id,
                userId: item.userId,
                servicemanId: item.servicemanId,
                bookingDate: item.bookingDate,
                bookedAt: item.bookedAt,
                status: item.status,
                serviceType: item.serviceType,
                address: item.address
            }));
            
            const reducedUsers = (usersData || []).map(item => ({
                id: item.id,
                name: item.name,
                email: item.email,
                phone: item.phone
            }));
            
            const reducedServicemen = (servicemenData || []).map(item => ({
                id: item.id,
                name: item.name,
                email: item.email,
                phone: item.phone
            }));

            const cacheData = {
                data: {
                    bookings: reducedBookings,
                    users: reducedUsers,
                    servicemen: reducedServicemen
                },
                timestamp: Date.now()
            };
            localStorage.setItem(BOOKINGS_CACHE_KEY, JSON.stringify(cacheData));
        } catch (e) {
            if (e.name === 'QuotaExceededError') {
                try {
                    localStorage.removeItem(BOOKINGS_CACHE_KEY);
                    localStorage.removeItem('customersDataCache');
                    localStorage.removeItem('servicemenDataCache');
                    localStorage.removeItem('dashboardDataCache');
                } catch (clearError) {
                    // Ignore
                }
            }
            // Silently fail - app will work without cache
        }
    };

    const storedState = getStoredState();
    const [currentPage, setCurrentPage] = useState(storedState?.currentPage || 1);
    const [sortBy, setSortBy] = useState("bookedAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const bookingsPerPage = 20;

    // Fetch all bookings from the top-level "Bookings" collection
    const fetchBookings = async (showLoader = true) => {
        // Prevent multiple simultaneous fetches
        if (isFetchingRef.current && showLoader) {
            return;
        }

        try {
            isFetchingRef.current = true;
            if (showLoader) {
                setLoading(true);
            }
            setError(null);

            // Fetch bookings from the top-level "Bookings" collection
            const bookingsSnapshot = await getDocs(collection(db, "Bookings"));
            const bookingsData = [];

            bookingsSnapshot.forEach((doc) => {
                bookingsData.push({ id: doc.id, ...doc.data() });
            });

            setBookings(bookingsData);

            // Fetch users and servicemen in parallel
            const [usersRes, servicemenRes] = await Promise.all([
                fetch(`${BASE_URL}/users`),
                fetch(`${BASE_URL}/servicemen`)
            ]);

            if (!usersRes.ok || !servicemenRes.ok) {
                throw new Error("Failed to fetch users or servicemen");
            }

            const [usersData, servicemenData] = await Promise.all([
                usersRes.json(),
                servicemenRes.json()
            ]);

            const usersArray = Array.isArray(usersData) ? usersData : [usersData];
            const servicemenArray = Array.isArray(servicemenData) ? servicemenData : [servicemenData];

            setUsers(usersArray);
            setServicemen(servicemenArray);
            
            // Save to cache
            saveToCache(bookingsData, usersArray, servicemenArray);
            
            if (showLoader) {
                setLoading(false);
            }
        } catch (error) {
            console.error("Error fetching bookings:", error);
            // Don't clear data if we have cached data
            const cached = getCachedBookings();
            if (!cached) {
                setError(error.message);
                setBookings([]);
                setUsers([]);
                setServicemen([]);
            }
            if (showLoader) {
                setLoading(false);
            }
        } finally {
            isFetchingRef.current = false;
        }
    };

    // Save state to sessionStorage
    const saveState = () => {
        try {
            const state = {
                currentPage,
                scrollPosition: scrollContainerRef.current?.scrollTop || window.scrollY || 0
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            console.error('Error saving state:', e);
        }
    };

    // Save scroll position on scroll
    useEffect(() => {
        const container = scrollContainerRef.current || window;
        const handleScroll = () => {
            const scrollPos = scrollContainerRef.current?.scrollTop || window.scrollY || 0;
            try {
                const state = {
                    currentPage,
                    scrollPosition: scrollPos
                };
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            } catch (e) {
                // Ignore storage errors
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [currentPage]);

    // Save state when page changes
    useEffect(() => {
        saveState();
    }, [currentPage]);

    useEffect(() => {
        // Load cached data immediately for instant display
        const cachedData = getCachedBookings();
        if (cachedData) {
            setBookings(cachedData.bookings || []);
            setUsers(cachedData.users || []);
            setServicemen(cachedData.servicemen || []);
            setLoading(false);
            // Fetch fresh data in background without showing loader
            fetchBookings(false);
        } else {
            // No cache, fetch with loader
            fetchBookings(true);
        }

        // Smart polling: only poll when tab is visible and reduce frequency
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Tab is hidden, clear interval
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            } else {
                // Tab is visible, start polling
                if (!intervalRef.current) {
                    intervalRef.current = setInterval(() => {
                        fetchBookings(false); // Background refresh without loader
                    }, 30000); // Poll every 30 seconds
                }
            }
        };

        // Start polling if tab is visible
        if (!document.hidden) {
            intervalRef.current = setInterval(() => {
                fetchBookings(false); // Background refresh without loader
            }, 30000); // Poll every 30 seconds
        }

        // Listen for visibility changes
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Restore scroll position after data loads
    useEffect(() => {
        if (!loading && storedState) {
            // Use setTimeout to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                const scrollPos = storedState.scrollPosition || 0;
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = scrollPos;
                } else {
                    window.scrollTo(0, scrollPos);
                }
                // Clear the stored state after restoring
                sessionStorage.removeItem(STORAGE_KEY);
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [loading, storedState]);

    // Helper function to get user name by userId
    const getUserName = (userId) => {
        const user = users.find(u => u.id === userId);
        return user?.name || "Unknown User";
    };

    // Helper function to get serviceman name by servicemanId
    const getServicemanName = (servicemanId) => {
        const serviceman = servicemen.find(s => s.id === servicemanId);
        return serviceman?.name || "Unknown Serviceman";
    };

    // Helper function to format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";

        let date;
        if (timestamp._seconds) {
            date = new Date(timestamp._seconds * 1000);
        } else if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleString();
    };

    // Helper function to format date only
    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";

        let date;
        if (timestamp._seconds) {
            date = new Date(timestamp._seconds * 1000);
        } else if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleDateString();
    };

    // Helper function to format date and time in AM/PM format
    const formatDateTime = (timestamp) => {
        if (!timestamp) return "N/A";

        let date;
        if (timestamp._seconds) {
            date = new Date(timestamp._seconds * 1000);
        } else if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp.toMillis) {
            date = new Date(timestamp.toMillis());
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            date = new Date(timestamp);
        }

        if (isNaN(date.getTime())) return "N/A";

        // Format: Date and Time in AM/PM format
        const dateStr = date.toLocaleDateString();
        const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `${dateStr} ${timeStr}`;
    };

    const filteredBookings = useMemo(() => {
        return bookings
            .filter(booking => {
                if (!searchTerm) return true;

                const search = searchTerm.toLowerCase();
                const bookingId = (booking.bookingId || "").toLowerCase();
                const profession = (booking.profession || "").toLowerCase();
                const userName = getUserName(booking.userId).toLowerCase();
                const servicemanName = getServicemanName(booking.servicemanId).toLowerCase();

                return bookingId.includes(search) ||
                    profession.includes(search) ||
                    userName.includes(search) ||
                    servicemanName.includes(search);
            })
            .sort((a, b) => {
                let aValue, bValue;

                if (sortBy === "bookingDate") {
                    aValue = a.bookingDate?._seconds || a.bookingDate?.toMillis?.() || 0;
                    bValue = b.bookingDate?._seconds || b.bookingDate?.toMillis?.() || 0;
                } else if (sortBy === "profession") {
                    aValue = (a.profession || "").toLowerCase();
                    bValue = (b.profession || "").toLowerCase();
                } else if (sortBy === "userName") {
                    aValue = getUserName(a.userId).toLowerCase();
                    bValue = getUserName(b.userId).toLowerCase();
                } else if (sortBy === "servicemanName") {
                    aValue = getServicemanName(a.servicemanId).toLowerCase();
                    bValue = getServicemanName(b.servicemanId).toLowerCase();
                } else {
                    aValue = a[sortBy] || "";
                    bValue = b[sortBy] || "";
                }

                if (sortOrder === "asc") {
                    return aValue > bValue ? 1 : -1;
                } else {
                    return aValue < bValue ? 1 : -1;
                }
            });
    }, [bookings, users, servicemen, searchTerm, sortBy, sortOrder]);

    // Summary stats similar to referrals cards
    const totalBookings = bookings.length;
    const filteredResults = filteredBookings.length;
    const todaysBookingsCount = useMemo(() => {
        const today = new Date().toDateString();
        return bookings.filter((b) => {
            const d = b.bookingDate?._seconds
                ? new Date(b.bookingDate._seconds * 1000)
                : b.bookingDate?.toDate
                    ? b.bookingDate.toDate()
                    : b.bookingDate?.toMillis
                        ? new Date(b.bookingDate.toMillis())
                        : b.bookingDate
                            ? new Date(b.bookingDate)
                            : null;
            if (!d || isNaN(d.getTime())) return false;
            return d.toDateString() === today;
        }).length;
    }, [bookings]);

    const paginatedBookings = useMemo(() => {
        const indexOfLastBooking = currentPage * bookingsPerPage;
        const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
        return {
            indexOfFirstBooking,
            currentBookings: filteredBookings.slice(indexOfFirstBooking, indexOfLastBooking),
            totalPages: Math.ceil(filteredBookings.length / bookingsPerPage),
        };
    }, [filteredBookings, currentPage]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Scroll to top of table when page changes
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Handlers to open user/serviceman details (similar to referrals screen)
    const handleUserClick = (userId) => {
        if (!userId) return;
        const user = users.find((u) => u.id === userId) || null;
        setSelectedUser(user);
        setSelectedServiceman(null);
    };

    const handleServicemanClick = async (servicemanId) => {
        if (!servicemanId) return;
        setModalLoading(true);
        setSelectedServiceman(null);
        setSelectedUser(null);
        try {
            // Fetch latest serviceman details from backend, fallback to cached list
            const response = await fetch(`${BASE_URL}/servicemen/${servicemanId}`);
            if (response.ok) {
                const data = await response.json();
                setSelectedServiceman(data);
            } else {
                const svc = servicemen.find((s) => s.id === servicemanId) || null;
                setSelectedServiceman(svc);
            }
        } catch (error) {
            console.error("Error fetching serviceman details:", error);
            const svc = servicemen.find((s) => s.id === servicemanId) || null;
            setSelectedServiceman(svc);
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setSelectedUser(null);
        setSelectedServiceman(null);
    };

    // Generate pagination page numbers with sliding window
    const getPaginationPages = () => {
        const totalPages = paginatedBookings.totalPages;
        const current = currentPage;
        const maxVisible = 10; // Maximum number of page buttons to show
        const pages = [];

        if (totalPages <= maxVisible) {
            // If total pages is less than max visible, show all
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            // Calculate window around current page
            let start = Math.max(2, current - Math.floor(maxVisible / 2));
            let end = Math.min(totalPages - 1, start + maxVisible - 3);

            // Adjust start if we're near the end
            if (end === totalPages - 1) {
                start = Math.max(2, totalPages - maxVisible + 2);
            }

            // Add ellipsis if there's a gap after first page
            if (start > 2) {
                pages.push('ellipsis-start');
            }

            // Add pages in the window
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            // Add ellipsis if there's a gap before last page
            if (end < totalPages - 1) {
                pages.push('ellipsis-end');
            }

            // Always show last page
            pages.push(totalPages);
        }

        return pages;
    };

    const handleSortChange = (e) => {
        const newSortBy = e.target.value;
        if (sortBy === newSortBy) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(newSortBy);
            setSortOrder("desc");
        }
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="loading">
                <span className="loader"></span>
                <style>{`
          .loading {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            color: var(--text-secondary, #666);
            flex-direction: column;
          }
          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #000;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          [data-theme="dark"] .loader::after {
            border: 2px solid #FFF;
          }
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Error Loading Bookings</h2>
                <p>{error}</p>
                <button onClick={() => fetchBookings(true)} style={{
                    padding: '10px 20px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '10px'
                }}>
                    Retry
                </button>
                <style>{`
                    .error-container {
                        padding: 40px;
                        text-align: center;
                        color: #dc2626;
                        background: #fef2f2;
                        border-radius: 8px;
                        margin: 20px;
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="bookings-panel" ref={scrollContainerRef}>
            <style>{`
        .bookings-panel {
          padding: 30px;
          margin: 0 auto;
          background: var(--bg-primary, #f9fafb);
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.05));
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }
        h2 {
          color: var(--text-primary, #1f2937);
          font-size: 24px;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .search-sort {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .search-input {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 4px;
          font-size: 14px;
          width: 250px;
          transition: all 0.2s;
          background: var(--bg-input, #ffffff);
          color: var(--text-primary, #111827);
        }
        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 4px rgba(59,130,246,0.3);
        }
        .sort-btn {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 4px;
          background: var(--bg-input, #fff);
          font-size: 14px;
          cursor: pointer;
          color: var(--text-primary, #374151);
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }
        .stat-card {
          background: var(--bg-card, #ffffff);
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.1));
          text-align: center;
        }
        .stat-icon {
          font-size: 2rem;
          margin-bottom: 10px;
          color: #3b82f6;
        }
        .stat-value {
          font-size: 2rem;
          font-weight: bold;
          color: var(--text-primary, #1f2937);
          margin: 0;
        }
        .stat-label {
          color: var(--text-muted, #6b7280);
          margin: 5px 0 0 0;
          font-size: 14px;
        }
        .clickable-name {
          cursor: pointer;
          color: #3b82f6;
          text-decoration: underline;
          transition: all 0.2s;
        }
        .clickable-name:hover {
          color: #2563eb;
          text-decoration: none;
        }
        .table-container {
          overflow-x: auto;
          min-height: 300px;
          background: var(--bg-card, #ffffff);
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.1));
        }
        table {
          width: 100%;
          border-collapse: collapse;
          background: var(--bg-card, #ffffff);
        }
        th {
          background: #374151;
          color: #ffffff;
          padding: 15px 12px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
          border-bottom: 2px solid var(--border-color, #4b5563);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        [data-theme="dark"] th {
          background: #1f2937;
          color: #ffffff;
          border-bottom-color: var(--border-color, #404040);
        }
        td {
          padding: 15px 12px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          font-size: 14px;
          color: var(--text-primary, #4a5568);
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          background: var(--bg-card, #ffffff);
        }
        .booking-row {
          transition: background 0.2s;
          background: var(--bg-card, #ffffff);
        }
        [data-theme="dark"] .booking-row {
          background: var(--bg-secondary, #2d2d2d);
        }
        .booking-row:hover {
          background: var(--bg-hover, #f9fafb);
        }
        [data-theme="dark"] .booking-row:hover {
          background: var(--bg-hover, #353535);
        }
        .pagination {
          margin-top: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: var(--text-primary, #4a5568);
          font-size: 14px;
        }
        .pagination-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          max-width: 100%;
        }
        .page-btn {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 4px;
          background: var(--bg-card, #fff);
          cursor: pointer;
          color: var(--text-primary, #374151);
          transition: all 0.2s;
          min-width: 40px;
          text-align: center;
          white-space: nowrap;
        }
        .page-btn:hover:not(.active):not(:disabled) {
          background: var(--bg-hover, #e5e7eb);
        }
        .page-btn.active {
          background: #3b82f6;
          color: #fff;
          border-color: #3b82f6;
        }
        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .page-ellipsis {
          padding: 8px 4px;
          color: #6b7280;
          font-weight: 500;
          user-select: none;
        }
        .no-results {
          text-align: center;
          padding: 40px;
          color: #6b7280;
          font-size: 16px;
        }
        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
        }
        .status-badge.pending {
          background: #fef3c7;
          color: #92400e;
        }
        .status-badge.completed {
          background: #dbeafe;
          color: #1e40af;
        }
        .status-badge.confirmed {
          background: #dcfce7;
          color: #166534;
        }
        .status-badge.cancelled {
          background: #fee2e2;
          color: #991b1b;
        }
        .refresh-btn {
          padding: '8px 12px';
          backgroundColor: '#10b981';
          color: 'white';
          border: 'none';
          borderRadius: '4px';
          cursor: 'pointer';
          fontSize: '14px';
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background: #ffffff;
          padding: 20px;
          border-radius: 8px;
          /* Increase modal width for better readability on large screens */
          max-width: 900px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          position: relative;
        }
        .profile-details {
          margin-top: 20px;
        }
        .profile-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #e5e7eb;
          gap: 16px;
        }
        .profile-item .label {
          font-weight: 600;
          color: #4b5563;
          min-width: 140px;
        }
        .profile-item .value {
          flex: 1;
          color: #111827;
        }
        .profile-pic-modal {
          width: 96px;
          height: 96px;
          border-radius: 9999px;
          object-fit: cover;
        }
        .modal-close {
          position: absolute;
          top: 10px;
          right: 10px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 18px;
        }
        @media (max-width: 768px) {
          .header {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
          }
          .search-sort {
            width: 100%;
            flex-wrap: wrap;
          }
          .search-input {
            width: 100%;
          }
        }
      `}</style>

            <div className="header">
                <h2>
                    <FaCalendar /> Bookings Management
                </h2>
                <div className="search-sort">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by booking ID, profession, user, or serviceman..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select className="sort-btn" value={sortBy} onChange={handleSortChange}>
                        <option value="bookingDate">Sort By Booking Date</option>
                        <option value="profession">Sort By Profession</option>
                        <option value="userName">Sort By User Name</option>
                        <option value="servicemanName">Sort By Serviceman Name</option>
                    </select>
                    <button
                        onClick={() => fetchBookings(true)}
                        style={{
                            padding: '8px 12px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Stats Cards similar to referrals screen */}
            <div className="stats-grid">
                <div className="stat-card">
                    <FaUsers className="stat-icon" />
                    <p className="stat-value">{totalBookings}</p>
                    <p className="stat-label">Total Bookings</p>
                </div>
                <div className="stat-card">
                    <FaUserPlus className="stat-icon" />
                    <p className="stat-value">{filteredResults}</p>
                    <p className="stat-label">Filtered Results</p>
                </div>
                <div className="stat-card">
                    <FaCalendar className="stat-icon" />
                    <p className="stat-value">{todaysBookingsCount}</p>
                    <p className="stat-label">Today's Bookings</p>
                </div>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Booking ID</th>
                            <th>Booking Date & Time</th>
                            <th>Profession</th>
                            <th>User (Name)</th>
                            <th>Serviceman (Name)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedBookings.currentBookings.length > 0 ? (
                            paginatedBookings.currentBookings.map((booking, index) => (
                                <tr key={booking.id || index} className="booking-row">
                                    <td>{booking.bookingId || "N/A"}</td>
                                    <td>{formatDateTime(booking.bookingDate)}</td>
                                    <td>{booking.profession || "N/A"}</td>
                                    <td>
                                        <span
                                            className="clickable-name"
                                            onClick={() => handleUserClick(booking.userId)}
                                        >
                                            {getUserName(booking.userId)}
                                        </span>
                                    </td>
                                    <td>
                                        <span
                                            className="clickable-name"
                                            onClick={() => handleServicemanClick(booking.servicemanId)}
                                        >
                                            {getServicemanName(booking.servicemanId)}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="no-results">
                                    {bookings.length === 0 ? (
                                        "No bookings found in the database"
                                    ) : (
                                        "No bookings found matching your search"
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <span>Showing {filteredBookings.length} Entries →</span>
                <div className="pagination-buttons">
                    <button
                        className="page-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    {getPaginationPages().map((page, index) => {
                        if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                            return (
                                <span key={`ellipsis-${index}`} className="page-ellipsis">
                                    ...
                                </span>
                            );
                        }
                        return (
                            <button
                                key={page}
                                className={`page-btn ${currentPage === page ? "active" : ""}`}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        );
                    })}
                    <button
                        className="page-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === paginatedBookings.totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* User/Serviceman Details Modal */}
            {(selectedUser || selectedServiceman) && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeModal}>
                            <FaTimes />
                        </button>
                        {modalLoading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                                <span className="loader"></span>
                                <style>{`
                                    .loader {
                                        width: 48px;
                                        height: 48px;
                                        border-radius: 50%;
                                        display: inline-block;
                                        position: relative;
                                        border: 2px solid #FF3D00;
                                        box-sizing: border-box;
                                        animation: rotation 1s linear infinite;
                                    }
                                    .loader::after {
                                        content: '';  
                                        box-sizing: border-box;
                                        position: absolute;
                                        left: 4px;
                                        top: 4px;
                                        border: 2px solid #000;
                                        width: 12px;
                                        height: 12px;
                                        border-radius: 50%;
                                    }
                                    [data-theme="dark"] .loader::after {
                                        border: 2px solid #FFF;
                                    }
                                    @keyframes rotation {
                                        0% {
                                            transform: rotate(0deg);
                                        }
                                        100% {
                                            transform: rotate(360deg);
                                        }
                                    }
                                `}</style>
                            </div>
                        ) : selectedUser ? (
                            <>
                                <h3>User Profile</h3>
                                <div className="profile-details">
                                    <div className="profile-item">
                                        <span className="label">Profile Picture</span>
                                        <div className="value">
                                            <img
                                                src={selectedUser.image || selectedUser.profilePicture || DEFAULT_IMAGE}
                                                alt="Profile"
                                                className="profile-pic-modal"
                                                onError={(e) => {
                                                    e.target.src = DEFAULT_IMAGE;
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Name</span>
                                        <span className="value">{selectedUser.name || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Email</span>
                                        <span className="value">{selectedUser.email || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Phone</span>
                                        <span className="value">
                                            {selectedUser.phoneNumber || selectedUser.phone || "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Location</span>
                                        <span className="value">
                                            {selectedUser.location || selectedUser.address || "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Primary Area</span>
                                        <span className="value">{selectedUser.areaName || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Device Type</span>
                                        <span className="value">
                                            {selectedUser.deviceType && selectedUser.deviceType.trim() !== ""
                                                ? selectedUser.deviceType
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Created At</span>
                                        <span className="value">
                                            {selectedUser.createdAt?._seconds
                                                ? new Date(selectedUser.createdAt._seconds * 1000).toLocaleString()
                                                : selectedUser.createdAt || "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Last Seen</span>
                                        <span className="value">
                                            {selectedUser.lastSeen?._seconds
                                                ? new Date(selectedUser.lastSeen._seconds * 1000).toLocaleString()
                                                : selectedUser.lastSeen || "N/A"}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : selectedServiceman ? (
                            <>
                                <h3>Serviceman Profile</h3>
                                <div className="profile-details">
                                    <div className="profile-item">
                                        <span className="label">Profile Picture</span>
                                        <div className="value">
                                            <img
                                                src={selectedServiceman.image || DEFAULT_IMAGE}
                                                alt="Profile"
                                                className="profile-pic-modal"
                                                onError={(e) => {
                                                    e.target.src = DEFAULT_IMAGE;
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Name</span>
                                        <span className="value">{selectedServiceman.name || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Email</span>
                                        <span className="value">{selectedServiceman.email || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Phone</span>
                                        <span className="value">
                                            {selectedServiceman.phone
                                                ? `+91-${selectedServiceman.phone}`
                                                : selectedServiceman.phoneNumber || "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Profession</span>
                                        <span className="value">{selectedServiceman.profession || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Date of Birth</span>
                                        <span className="value">
                                            {selectedServiceman.dateOfBirth?._seconds
                                                ? new Date(selectedServiceman.dateOfBirth._seconds * 1000).toLocaleDateString()
                                                : selectedServiceman.dateOfBirth || "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Location</span>
                                        <span className="value">
                                            {selectedServiceman.location ||
                                                selectedServiceman.address ||
                                                "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Primary Area</span>
                                        <span className="value">{selectedServiceman.areaName || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Device Type</span>
                                        <span className="value">
                                            {selectedServiceman.deviceType &&
                                            selectedServiceman.deviceType.trim() !== ""
                                                ? selectedServiceman.deviceType
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Services</span>
                                        <div className="value">
                                            {selectedServiceman.services?.length > 0 ? (
                                                <ul className="services-list-modal">
                                                    {selectedServiceman.services.map((service, idx) => (
                                                        <li key={idx}>{service}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                "No services listed"
                                            )}
                                        </div>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Availability (Start-End)</span>
                                        <span className="value">
                                            {selectedServiceman.availabilityStart?._seconds &&
                                            selectedServiceman.availabilityEnd?._seconds
                                                ? `${new Date(
                                                      selectedServiceman.availabilityStart._seconds * 1000
                                                  ).toLocaleTimeString()} - ${new Date(
                                                      selectedServiceman.availabilityEnd._seconds * 1000
                                                  ).toLocaleTimeString()}`
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Average Rating</span>
                                        <span className="value">
                                            {selectedServiceman.avgRating
                                                ? `${selectedServiceman.avgRating}/5`
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Review Count</span>
                                        <span className="value">
                                            {selectedServiceman.reviewCount || 0}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Description</span>
                                        <span className="value">
                                            {selectedServiceman.description || "N/A"}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Bookings;

